'use client';

/**
 * @fileoverview Mobile bottom-sheet variant of {@link CategoriesDropdown}
 * (FRE-823).
 *
 * Rendered by `CategoriesDropdown` on `< md` viewports (≤ 767px) and on any
 * device matching `@media (hover: none)`. Same trigger button as the desktop
 * variant; only the panel shape changes.
 *
 * Built on the `Sheet` primitive (Shadcn / Radix Dialog wrapper) with
 * `side="bottom"` for the slide-up animation. The trigger button itself is
 * NOT rendered here — `CategoriesDropdown` owns the trigger and passes
 * controlled open/close state in so a single visual button drives both
 * variants without flicker on viewport changes.
 *
 * Spec contracts:
 *   - Backdrop: `rgba(15,20,31,0.65)` + `backdrop-filter: blur(6px)` per the
 *     v1.1.0 modal spec.
 *   - Each category row is a `<Link>` with ≥44px tap target.
 *   - ESC, backdrop tap, and row selection all dismiss (the latter via the
 *     same `onOpenChange(false)` path Radix uses for the former two).
 *   - Focus trap + body scroll lock provided by the Radix Dialog wrapper.
 */

import * as React from 'react';
import Link from 'next/link';
import { Sheet, SheetContent } from '@/components/atoms/primitives/sheet';
import { cn } from '@/components/atoms/primitives/utils';
import { categoryDisplayLabel } from '@/lib/city-home/categoryLabelCase';
import { CATEGORIES } from '@/utils/categoryConstants';

/**
 * v1.1.0 modal spec backdrop. Inlined as Tailwind arbitrary classes so the
 * literal spec values stay co-located with the component instead of leaking
 * into a global CSS file. The companion mobile-variant test asserts the
 * literal class strings (`bg-[rgb(15_20_31_/_0.65)]`, `backdrop-blur-[6px]`)
 * so any drift from the spec fails CI before merge.
 */
const OVERLAY_CLASSNAME = cn(
  'bg-[rgb(15_20_31_/_0.65)] backdrop-blur-[6px]',
  // `Sheet`'s default overlay class is `z-50` (sits above the app-shell
  // bottom-nav, which is also `z-50`); we inherit that stacking via the
  // primitive and only contribute the v1.1.0 spec backdrop styling
  // (rgba + blur) through the `overlayClassName` slot.
);

/**
 * Content styling — bottom-anchored sheet that takes auto height (the row
 * stack is short) with safe-area padding for notched devices. The Shadcn
 * `Sheet` primitive emits `data-state=open/closed` for the slide animation,
 * so consumers don't need to wire `data-state`-keyed classes themselves.
 */
const CONTENT_CLASSNAME = cn(
  // Reset the default Sheet padding so our own row layout owns the gutter.
  'gap-0 p-0',
  // Round only the top corners — the sheet butts the viewport bottom.
  'rounded-t-2xl',
  // Bottom safe-area padding so the last row clears the home-bar on iOS.
  'pb-[env(safe-area-inset-bottom)]',
);

export interface CategoriesMobileSheetProps {
  /** Whether the sheet is open. Controlled by `CategoriesDropdown`. */
  open: boolean;
  /** Radix open/close callback (Escape, backdrop click, programmatic). */
  onOpenChange: (open: boolean) => void;
  /**
   * Builds the destination href for a category slug. Supplied by
   * `CategoriesDropdown` so the desktop dropdown and mobile sheet always emit
   * the same surface-correct destination. See `CategoriesDropdown` for the
   * per-surface contract.
   */
  buildCategoryHref: (slug: string) => string;
  /** Slug of the currently active category, if any (sets `data-active`). */
  activeSlug?: string;
}

/**
 * Bottom-sheet variant of CategoriesDropdown. See file-level docstring.
 */
export function CategoriesMobileSheet({
  open,
  onOpenChange,
  buildCategoryHref,
  activeSlug,
}: CategoriesMobileSheetProps): React.JSX.Element | null {
  /**
   * Close-on-select: each row's `onClick` calls `onOpenChange(false)`. The
   * underlying `<Link>` navigation continues (no `preventDefault`), so the
   * router push and the dismiss happen in the same tick.
   */
  const handleSelect = React.useCallback((): void => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        title="Categories"
        description="Browse events by category."
        overlayClassName={OVERLAY_CLASSNAME}
        className={CONTENT_CLASSNAME}
        // `id` matches the `aria-controls` on the CategoriesDropdown trigger
        // (`<button aria-controls="categories-mobile-sheet">`), so assistive
        // tech can resolve the controlled element when the sheet is mounted.
        id="categories-mobile-sheet"
        data-testid="categories-mobile-sheet"
      >
        {/* The Sheet primitive renders SheetOverlay before SheetContent in
            the portal. To give the test a stable handle on the backdrop, we
            also annotate the overlay via overlayClassName — but Radix's
            `data-state` and `data-slot="sheet-overlay"` are not test-ids.
            Mirror the test-id onto the overlay through a sibling node so
            the spec contract (rgba + blur) is observable without modifying
            the shared `sheet` primitive. */}
        <BackdropTestIdShim />

        {/* Visually-hidden heading is provided by SheetContent's `title`
            prop above (rendered as a Radix `Dialog.Title` with `.sr-only`),
            which keeps the dialog announceable without cluttering the
            visual layout. */}
        <nav aria-label="Categories" className="flex flex-col">
          {CATEGORIES.map((category) => {
            const Icon = category.icon;
            const isActive = activeSlug === category.slug;
            return (
              <Link
                key={category.slug}
                href={buildCategoryHref(category.slug)}
                onClick={handleSelect}
                data-testid={`categories-mobile-sheet-item-${category.slug}`}
                data-active={isActive ? 'true' : undefined}
                className={cn(
                  // Tap target — ≥44px enforced; px-5 keeps a 20px gutter
                  // inside the rounded-t-2xl panel.
                  'flex min-h-[44px] items-center gap-3 px-5 py-3',
                  // Type & color — token-driven so dark/light themes render
                  // intentionally.
                  'text-base font-medium text-foreground',
                  // Border between rows; hidden on the first row.
                  '[&:not(:first-child)]:border-t [&:not(:first-child)]:border-white/[0.08]',
                  // Press feedback — coarse-pointer-friendly active state.
                  'active:bg-white/[0.04]',
                  // Focus-visible ring for keyboard users (sheets can be
                  // reached via Tab once open).
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  // Active category gets a subtle weight bump. (The desktop
                  // dropdown variant also has a skyline accent underline; on
                  // mobile we keep the chrome minimal so the tap target stays
                  // a clean row.)
                  'data-[active=true]:font-semibold',
                )}
              >
                <Icon className="size-5 shrink-0" aria-hidden="true" />
                <span className="flex-1">{categoryDisplayLabel(category.name)}</span>
              </Link>
            );
          })}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Tags the most recently mounted Radix `SheetOverlay` (rendered as a sibling
 * inside the portal) with a `data-testid` and the v1.1.0 backdrop class so
 * the test contract is observable without forking the shared `sheet`
 * primitive.
 *
 * This is a pragmatic shim: the Shadcn `Sheet` primitive applies
 * `overlayClassName` to the overlay but exposes no `data-testid` slot. To
 * keep the contract test stable (and explicit about WHICH overlay is the
 * categories backdrop when multiple sheets might coexist), we look up the
 * overlay via `data-slot="sheet-overlay"` on mount and stamp the testid
 * before the test queries it.
 *
 * The class assertions in the test read directly from this node's
 * `className`, which already carries the spec values via
 * `overlayClassName` — the shim only adds the testid hook.
 */
function BackdropTestIdShim(): null {
  React.useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    // Find the latest sheet-overlay node — Radix mounts overlays as the
    // first child of the portal. We tag only the one whose adjacent
    // SheetContent is OUR sheet (data-testid="categories-mobile-sheet"),
    // so unrelated sheets in the same tree don't get re-tagged.
    const content = document.querySelector('[data-testid="categories-mobile-sheet"]');
    if (!content) return;
    // Radix portals render overlay + content as siblings inside the same
    // portal root. Walk up to the portal parent, then find its overlay
    // child.
    const portalRoot = content.parentElement;
    const overlay = portalRoot?.querySelector<HTMLElement>('[data-slot="sheet-overlay"]');
    if (overlay && !overlay.dataset.testid) {
      overlay.dataset.testid = 'categories-mobile-sheet-backdrop';
    }
  }, []);
  return null;
}
